import { useEffect, useState } from "react";
import { Check, X, Ban, Loader2, Inbox, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { AppointmentEditor } from "./AppointmentEditor";

const STATUS = {
  pending: { label: "En attente", cls: "bg-gold/15 text-taupe-dark" },
  confirmed: { label: "Confirmé", cls: "bg-[#4A6B53]/10 text-[#4A6B53]" },
  refused: { label: "Refusé", cls: "bg-[#9E4747]/10 text-[#9E4747]" },
  cancelled: { label: "Annulé", cls: "bg-espresso/5 text-espresso/50" },
};

export const Requests = () => {
  const [appointments, setAppointments] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [services, setServices] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    api.get("/admin/services").then((r) => setServices(r.data.services.filter((s) => s.active))).catch(() => {});
  }, []);

  const load = () => {
    api
      .get("/admin/appointments", { params: filter ? { status: filter } : {} })
      .then((r) => setAppointments(r.data.appointments))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(load, [filter]);

  const decide = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/admin/appointments/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Rendez-vous confirmé" : "Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-requests-tab">
      <div className="flex flex-wrap gap-2 mb-6">
        {[["pending", "En attente"], ["confirmed", "Confirmés"], ["refused", "Refusés"], ["", "Tous"]].map(([v, l]) => (
          <button
            key={v}
            data-testid={`filter-${v || "all"}`}
            onClick={() => setFilter(v)}
            className={`rounded-full px-5 py-2 text-xs tracking-[0.1em] uppercase border transition-colors duration-300 ${
              filter === v ? "bg-espresso text-cream border-espresso" : "border-taupe/30 text-espresso/60 hover:border-taupe"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      {!appointments ? (
        <div className="flex items-center gap-3 text-sm text-espresso/55 py-10">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-16 text-espresso/45" data-testid="requests-empty">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-taupe/50" strokeWidth={1.5} />
          <p className="text-sm">Aucune demande pour ce filtre.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {appointments.map((a) => (
            <div key={a.id}>
              <div
                data-testid={`request-card-${a.id}`}
                className="bg-white border border-taupe/20 rounded-2xl p-5 flex flex-wrap items-center gap-4 justify-between"
              >
              <div className="min-w-[220px]">
                <p className="font-display text-xl text-espresso">{a.service_name}</p>
                <p className="text-xs text-espresso/55 mt-1">
                  {a.date} à {a.time} · {a.duration} min · {a.price_label}
                </p>
              </div>
              <div className="min-w-[180px]">
                <p className="text-sm text-espresso font-medium">{a.name}</p>
                <p className="text-xs text-espresso/55">{a.phone}</p>
                {a.message && <p className="text-xs text-espresso/45 italic mt-1">“{a.message}”</p>}
              </div>
              <span className={`rounded-full px-4 py-1.5 text-xs font-medium ${STATUS[a.status]?.cls}`} data-testid={`status-${a.id}`}>
                {STATUS[a.status]?.label}
              </span>
              {a.presence_confirmed && (
                <span
                  className="rounded-full bg-[#4A6B53]/10 text-[#4A6B53] px-4 py-1.5 text-xs font-medium"
                  data-testid={`presence-badge-${a.id}`}
                >
                  Présence confirmée
                </span>
              )}
              <div className="flex gap-2">
                <button
                  data-testid={`edit-btn-${a.id}`}
                  onClick={() => setEditingId(editingId === a.id ? null : a.id)}
                  title="Modifier le rendez-vous"
                  className={`rounded-full border px-4 py-2 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-1.5 transition-colors duration-300 ${
                    editingId === a.id ? "border-espresso bg-espresso text-cream" : "border-taupe/30 text-espresso/60 hover:border-taupe"
                  }`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Modifier
                </button>
                {a.status !== "confirmed" && (
                  <button
                    data-testid={`confirm-btn-${a.id}`}
                    disabled={busyId === a.id}
                    onClick={() => decide(a.id, "confirmed")}
                    className="rounded-full bg-[#4A6B53] text-white px-4 py-2 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-1.5 hover:brightness-110 transition-[filter] duration-300 disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" /> Confirmer
                  </button>
                )}
                {a.status !== "refused" && (
                  <button
                    data-testid={`refuse-btn-${a.id}`}
                    disabled={busyId === a.id}
                    onClick={() => decide(a.id, "refused")}
                    className="rounded-full border border-[#9E4747]/40 text-[#9E4747] px-4 py-2 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-1.5 hover:bg-[#9E4747]/5 transition-colors duration-300 disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" /> Refuser
                  </button>
                )}
                {a.status === "confirmed" && (
                  <button
                    data-testid={`cancel-btn-${a.id}`}
                    disabled={busyId === a.id}
                    onClick={() => decide(a.id, "cancelled")}
                    className="rounded-full border border-taupe/30 text-espresso/60 px-4 py-2 text-xs tracking-[0.08em] uppercase inline-flex items-center gap-1.5 hover:border-taupe transition-colors duration-300 disabled:opacity-50"
                  >
                    <Ban className="w-3.5 h-3.5" /> Annuler
                  </button>
                )}
              </div>
              </div>
              {editingId === a.id && (
                <div className="mt-2">
                  <AppointmentEditor
                    appt={a}
                    services={services}
                    onClose={() => setEditingId(null)}
                    onSaved={() => { setEditingId(null); load(); }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
